/**
 * Org-aware prayer actions usable from ANY view (group wall or the combined
 * cross-group views), independent of the current org context.
 *
 * REST actions: `setOrganizationContextSecure` resolves the org from the
 * request body for non-GET requests (body outranks the X-Organization-Id
 * header the axios interceptor always sets), so cross-org actions work
 * without rotating the org context — membership is verified server-side.
 *
 * Reactions: written straight to Firestore exactly like the Expo app
 * (PrayerContext.addEncouragementReaction) — the prayers update rule allows
 * group members to touch only ['encouragementReactions', 'updatedAt'] and is
 * membership-doc gated, so this also works cross-org.
 *
 * Optimistic updates patch BOTH react-query cache families:
 *  - ['prayers', groupId, status]      (infinite query: { pages: [{ prayers }] })
 *  - ['combined-prayers', groupId, status] (plain page: { prayers })
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  arrayRemove,
  arrayUnion,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { api } from '@/lib/apiClient';
import { getCurrentOrgId } from '@/lib/storage';
import { useAuth } from '@/context/AuthContext';
import type { Prayer, PrayerStatus } from '@flock-types/prayer.types';

const PRAYERS_COLLECTION = 'prayers';

type PrayerUpdater = (p: Prayer) => Prayer;

export interface UsePrayerActionsReturn {
  pray: (prayer: Prayer) => Promise<void>;
  encourage: (prayer: Prayer, note: string) => Promise<void>;
  liftToggle: (prayer: Prayer) => Promise<void>;
  markAnswered: (prayer: Prayer, answerText?: string) => Promise<void>;
  edit: (prayer: Prayer, prayerText: string) => Promise<void>;
  remove: (prayer: Prayer) => Promise<void>;
  toggleEncouragementReaction: (
    prayer: Prayer,
    noteId: string,
    emoji: string,
    reacted: boolean
  ) => Promise<void>;
}

export function usePrayerActions(): UsePrayerActionsReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  /** Patch one prayer across every prayer cache (group walls + combined views). */
  const patchPrayerCaches = useCallback(
    (prayerId: string, update: PrayerUpdater) => {
      const patchList = (prayers: Prayer[]): Prayer[] =>
        prayers.map((p) => (p.id === prayerId ? update(p) : p));

      for (const family of ['prayers', 'combined-prayers']) {
        queryClient.setQueriesData({ queryKey: [family] }, (old: any) => {
          if (!old) return old;
          // Infinite query shape
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) =>
                page?.prayers ? { ...page, prayers: patchList(page.prayers) } : page
              ),
            };
          }
          // Plain page shape ({ prayers: [...] })
          if (old.prayers) {
            return { ...old, prayers: patchList(old.prayers) };
          }
          return old;
        });
      }
    },
    [queryClient]
  );

  const invalidatePrayerCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['prayers'] });
    void queryClient.invalidateQueries({ queryKey: ['combined-prayers'] });
  }, [queryClient]);

  /** Body payload that pins the request to the prayer's org when it differs. */
  const orgBody = (prayer: Prayer): { organizationId?: string } =>
    prayer.organizationId && prayer.organizationId !== getCurrentOrgId()
      ? { organizationId: prayer.organizationId }
      : {};

  const pray = useCallback(
    async (prayer: Prayer): Promise<void> => {
      if (!user) throw new Error('User not authenticated');
      if (prayer.prayedForBy.includes(user.id)) return;

      patchPrayerCaches(prayer.id, (p) => ({
        ...p,
        prayedForBy: p.prayedForBy.includes(user.id) ? p.prayedForBy : [...p.prayedForBy, user.id],
        prayedForCount: p.prayedForBy.includes(user.id) ? p.prayedForCount : p.prayedForCount + 1,
      }));
      try {
        await api.put(`/prayers/${prayer.id}/mark-prayed`, { ...orgBody(prayer) });
      } catch (e) {
        invalidatePrayerCaches();
        throw e;
      }
    },
    [user, patchPrayerCaches, invalidatePrayerCaches]
  );

  const encourage = useCallback(
    async (prayer: Prayer, note: string): Promise<void> => {
      if (!user) throw new Error('User not authenticated');

      patchPrayerCaches(prayer.id, (p) => ({
        ...p,
        encouragementNotes: [
          ...p.encouragementNotes,
          {
            id: `temp-${Date.now()}`,
            userId: user.id,
            userName: user.name,
            note,
            createdAt: new Date().toISOString(),
          },
        ],
      }));
      try {
        await api.post(`/prayers/${prayer.id}/encouragement`, { note, ...orgBody(prayer) });
      } finally {
        // Refetch either way: replaces the temp- note id with the real one
        invalidatePrayerCaches();
      }
    },
    [user, patchPrayerCaches, invalidatePrayerCaches]
  );

  const liftToggle = useCallback(
    async (prayer: Prayer): Promise<void> => {
      const isCurrentlyLifted = Boolean(
        prayer.liftedAt &&
          Date.now() - new Date(prayer.liftedAt as string).getTime() < 48 * 3600_000
      );

      patchPrayerCaches(prayer.id, (p) =>
        isCurrentlyLifted
          ? { ...p, liftedAt: undefined }
          : { ...p, liftedAt: new Date().toISOString(), liftedCount: (p.liftedCount || 0) + 1 }
      );
      try {
        await api.put(
          `/prayers/${prayer.id}/${isCurrentlyLifted ? 'unlift' : 'lift'}`,
          { ...orgBody(prayer) }
        );
      } catch (e) {
        invalidatePrayerCaches();
        throw e;
      }
    },
    [patchPrayerCaches, invalidatePrayerCaches]
  );

  const markAnswered = useCallback(
    async (prayer: Prayer, answerText?: string): Promise<void> => {
      patchPrayerCaches(prayer.id, (p) => ({
        ...p,
        status: 'answered' as PrayerStatus,
        answerText,
        answeredAt: new Date().toISOString(),
      }));
      try {
        await api.put(`/prayers/${prayer.id}/mark-answered`, { answerText, ...orgBody(prayer) });
      } catch (e) {
        invalidatePrayerCaches();
        throw e;
      }
    },
    [patchPrayerCaches, invalidatePrayerCaches]
  );

  const edit = useCallback(
    async (prayer: Prayer, prayerText: string): Promise<void> => {
      patchPrayerCaches(prayer.id, (p) => ({ ...p, prayerText }));
      try {
        await api.put(`/prayers/${prayer.id}/text`, { prayerText, ...orgBody(prayer) });
      } catch (e) {
        invalidatePrayerCaches();
        throw e;
      }
    },
    [patchPrayerCaches, invalidatePrayerCaches]
  );

  const remove = useCallback(
    async (prayer: Prayer): Promise<void> => {
      const dropFromList = (prayers: Prayer[]) => prayers.filter((p) => p.id !== prayer.id);
      for (const family of ['prayers', 'combined-prayers']) {
        queryClient.setQueriesData({ queryKey: [family] }, (old: any) => {
          if (!old) return old;
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) =>
                page?.prayers ? { ...page, prayers: dropFromList(page.prayers) } : page
              ),
            };
          }
          if (old.prayers) return { ...old, prayers: dropFromList(old.prayers) };
          return old;
        });
      }
      try {
        // DELETE carries the org pin in the request body (axios `data` config)
        await api.delete(`/prayers/${prayer.id}`, { data: { ...orgBody(prayer) } });
      } catch (e) {
        invalidatePrayerCaches();
        throw e;
      }
    },
    [queryClient, invalidatePrayerCaches]
  );

  const toggleEncouragementReaction = useCallback(
    async (prayer: Prayer, noteId: string, emoji: string, reacted: boolean): Promise<void> => {
      const firebaseUid = auth.currentUser?.uid;
      if (!firebaseUid) return;
      if (noteId.startsWith('temp-')) return; // optimistic note not persisted yet

      // Optimistic patch of the nested reactions map (mirrors Expo's cleanup
      // of empty emoji/note keys on removal).
      patchPrayerCaches(prayer.id, (p) => {
        const reactions = { ...(p.encouragementReactions || {}) };
        const noteReactions = { ...(reactions[noteId] || {}) };
        if (reacted) {
          const remaining = (noteReactions[emoji] || []).filter((uid) => uid !== firebaseUid);
          if (remaining.length === 0) delete noteReactions[emoji];
          else noteReactions[emoji] = remaining;
          if (Object.keys(noteReactions).length === 0) delete reactions[noteId];
          else reactions[noteId] = noteReactions;
        } else {
          const users = noteReactions[emoji] || [];
          if (!users.includes(firebaseUid)) noteReactions[emoji] = [...users, firebaseUid];
          reactions[noteId] = noteReactions;
        }
        return { ...p, encouragementReactions: reactions };
      });

      try {
        await updateDoc(doc(db, PRAYERS_COLLECTION, prayer.id), {
          [`encouragementReactions.${noteId}.${emoji}`]: reacted
            ? arrayRemove(firebaseUid)
            : arrayUnion(firebaseUid),
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.warn('[usePrayerActions] Reaction write failed:', (e as Error).message);
        invalidatePrayerCaches();
      }
    },
    [patchPrayerCaches, invalidatePrayerCaches]
  );

  return { pray, encourage, liftToggle, markAnswered, edit, remove, toggleEncouragementReaction };
}
